import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { format } from 'date-fns';
import { DesktopBluetoothService } from 'src/app/services/desktop-bluetooth.service';
import { DesktopService } from 'src/app/services/desktop.service';
import { DesktopUsbService } from 'src/app/services/desktop-usb.service';

export interface AvailablePrinter {
  name: string;
  connectionType: 'usb' | 'bluetooth' | 'windows' | string;
  id?: string;
  address?: string;
  printerName?: string;
  portName?: string;
  isConnected?: boolean;
}

@Component({
  templateUrl: './desktop-printers.component.html',
  styleUrls: ['./desktop-printers.component.scss'],
})
export class DesktopPrintersComponent {
  printers: AvailablePrinter[] = [];
  selectedPrinter: AvailablePrinter;

  constructor(
    private modalCtrl: ModalController,
    private desktop: DesktopService,
    private desktopBluetooth: DesktopBluetoothService,
    private desktopUsb: DesktopUsbService
  ) {}

  getConnectionIcon(type: string): string {
    switch ((type || '').toLowerCase()) {
      case 'usb':
        return 'usb-outline';
      case 'bluetooth':
      case 'bt':
      case 'btle':
        return 'bluetooth-outline';
      case 'windows':
      case 'desktop':
        return 'desktop-outline';
      case 'net':
      case 'lan':
        return 'globe-outline';
      default:
        return 'print-outline';
    }
  }

  getConnectionLabel(type: string): string {
    switch ((type || '').toLowerCase()) {
      case 'usb':
        return 'USB';
      case 'bluetooth':
      case 'bt':
      case 'btle':
        return 'Bluetooth';
      case 'windows':
      case 'desktop':
        return 'Windows';
      case 'net':
      case 'lan':
        return 'Network';
      default:
        return type || '';
    }
  }

  canTestPrint(printer: AvailablePrinter): boolean {
    if (!printer) {
      return false;
    }

    return (
      printer.connectionType === 'bluetooth' ||
      printer.connectionType === 'usb' ||
      !!this.getWindowsPrinterName(printer)
    );
  }

  canAddPrinter(printer: AvailablePrinter): boolean {
    if (!printer) {
      return false;
    }

    if (printer.connectionType === 'bluetooth') {
      return !!(printer.id || printer.address);
    }

    if (printer.connectionType === 'usb') {
      return !!(printer.id || printer.address);
    }

    return !!this.getWindowsPrinterName(printer);
  }

  trackPrinter(index: number, printer: AvailablePrinter): string {
    return [
      printer?.connectionType || '',
      printer?.id || printer?.address || printer?.printerName || printer?.name || '',
      String(index),
    ].join(':');
  }

  selectPrinter(p: AvailablePrinter) {
    this.selectedPrinter = p;
  }

  addPrinter() {
    if (!this.canAddPrinter(this.selectedPrinter)) {
      return;
    }

    const isBluetooth = this.selectedPrinter.connectionType === 'bluetooth';
    const isDirectUsb = this.selectedPrinter.connectionType === 'usb';
    const windowsName = this.getWindowsPrinterName(this.selectedPrinter);

    this.modalCtrl.dismiss({
      printer: isBluetooth || isDirectUsb
        ? this.getDisplayName(this.selectedPrinter)
        : windowsName,
      type: isBluetooth ? 'bt' : isDirectUsb ? 'usb' : 'desktop',
      address: isBluetooth || isDirectUsb
        ? this.selectedPrinter.id || this.selectedPrinter.address
        : windowsName,
    });
  }

  async testPrint() {
    if (!this.selectedPrinter) {
      return;
    }

    if (this.selectedPrinter.connectionType === 'bluetooth') {
      const deviceId =
        this.selectedPrinter.id || this.selectedPrinter.address;
      if (!deviceId) {
        return;
      }

      const text = `\n\nTurboPOS ${format(new Date(), 'dd MMM yyyy HH:mm')}\nТестовий друк\n\n`;
      await this.desktopBluetooth.sendDataToDevice(deviceId, text);
      return;
    }

    if (this.selectedPrinter.connectionType === 'usb') {
      const deviceId =
        this.selectedPrinter.id || this.selectedPrinter.address;
      if (!deviceId) {
        return;
      }

      const text = `\n\nTurboPOS ${format(new Date(), 'dd MMM yyyy HH:mm')}\nТестовий друк\n\n`;
      await this.desktopUsb.sendDataToDevice(deviceId, text);
      return;
    }

    const printerName = this.getWindowsPrinterName(this.selectedPrinter);
    if (!printerName) {
      return;
    }

    const date = format(new Date(), 'dd MMM yyyy HH:mm');
    const text = `\n\nTurboPOS ${date}\nТестовий друк\n\nЄІЇєії`;
    this.desktop.send({
      Method: 'printText',
      Printer: printerName,
      Text: text,
      Width: 183,
    });
  }

  openList() {
    this.selectedPrinter = undefined;
  }

  close() {
    this.modalCtrl.dismiss();
  }

  getDisplayName(printer: AvailablePrinter): string {
    return printer?.printerName || printer?.name || '';
  }

  private getWindowsPrinterName(printer: AvailablePrinter): string {
    if (!printer) {
      return '';
    }

    if (
      printer.connectionType === 'bluetooth' ||
      printer.connectionType === 'usb'
    ) {
      return '';
    }

    return printer.printerName || '';
  }
}
